'use client'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'

// Dash + gap, the distance tailwind.config.ts dashMarch shifts per loop
const DASH_SEGMENT = 22

interface RequestProductCardProps {
  query: string
}

export function RequestProductCard({ query }: RequestProductCardProps) {
  const term = query.trim()
  const href = term
    ? `/marketplace/request-product?request=${encodeURIComponent(term)}`
    : '/marketplace/request-product'
  const outlineRef = useRef<SVGRectElement>(null)
  const [pathLength, setPathLength] = useState<number>()

  // Whole dash segments only, or the leftover partial dash piles up at the top-left corner where the path starts
  useEffect(() => {
    const outline = outlineRef.current
    if (!outline || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => {
      const segments = Math.max(1, Math.round(outline.getTotalLength() / DASH_SEGMENT))
      setPathLength(segments * DASH_SEGMENT)
    })
    observer.observe(outline.ownerSVGElement ?? outline)
    return () => observer.disconnect()
  }, [])

  return (
    <article className="group relative flex flex-col gap-4 rounded-lg border border-transparent p-6">
      {/* A CSS dashed border can't set dash length or gap, so the outline is drawn as an SVG stroke */}
      <svg
        aria-hidden="true"
        className="pointer-events-none absolute -inset-px h-[calc(100%+2px)] w-[calc(100%+2px)] text-primary-ink-30">
        {/* Inset half a pixel so the 1px stroke lands on whole pixels instead of blurring across two */}
        <rect
          ref={outlineRef}
          x="0.5"
          y="0.5"
          rx="7.5"
          pathLength={pathLength}
          fill="none"
          stroke="currentColor"
          strokeWidth="1"
          strokeDasharray="14 8"
          className="h-[calc(100%-1px)] w-[calc(100%-1px)] motion-safe:group-focus-within:animate-dash-march motion-safe:group-hover:animate-dash-march"
        />
      </svg>
      <span
        aria-hidden="true"
        className="flex h-10 w-10 items-center justify-center rounded-md border border-primary-ink-10 bg-primary-ink-6 text-primary-ink-100">
        <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
          <path d="M12 4.5v15m7.5-7.5h-15" strokeLinecap="round" />
        </svg>
      </span>

      <div>
        <p className="text-[13px] font-medium uppercase leading-5 tracking-[0.5px] text-primary-ink-85">
          Request a product
        </p>
        <h2 className="min-h-12 text-lg font-medium leading-6 tracking-[-0.3px] text-primary-ink-100">
          Don&rsquo;t see a product you need?
        </h2>
      </div>

      {/* Pinned to the bottom with the button so it lines up with a product card's description in the same row */}
      <p className="mt-auto min-h-9 text-xs leading-[18px] tracking-[-0.15px] text-primary-ink-100">
        Tell us what you&rsquo;re looking for and our team will get back to you with availability and pricing.
      </p>

      <Link
        href={href}
        className="flex h-8 w-full items-center justify-center gap-1.5 rounded-md border border-primary-ink-10 bg-white px-3 text-sm font-medium tracking-[-0.25px] text-primary-ink-100 transition-colors hover:border-primary-ink-30 motion-reduce:transition-none">
        {/* Castle UI mail glyph, matching the quote form */}
        <svg aria-hidden="true" className="h-4 w-4" viewBox="0 0 24 24" fill="none">
          <path
            d="M4 20C3.45 20 2.97917 19.8042 2.5875 19.4125C2.19583 19.0208 2 18.55 2 18V6C2 5.45 2.19583 4.97917 2.5875 4.5875C2.97917 4.19583 3.45 4 4 4H20C20.55 4 21.0208 4.19583 21.4125 4.5875C21.8042 4.97917 22 5.45 22 6V18C22 18.55 21.8042 19.0208 21.4125 19.4125C21.0208 19.8042 20.55 20 20 20H4ZM20 8L12.525 12.675C12.4417 12.725 12.3542 12.7625 12.2625 12.7875C12.1708 12.8125 12.0833 12.825 12 12.825C11.9167 12.825 11.8292 12.8125 11.7375 12.7875C11.6458 12.7625 11.5583 12.725 11.475 12.675L4 8V18H20V8ZM12 11L20 6H4L12 11ZM4 8.25V6.775V6.8V6.7875V8.25Z"
            fill="currentColor"
          />
        </svg>
        Contact us
      </Link>
    </article>
  )
}
